import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FaCalendarCheck,
  FaCalendarDay,
  FaUsers,
  FaCheckCircle,
  FaHandsHelping,
  FaPaperPlane,
  FaPercent,
  FaPhoneAlt,
  FaUserCheck,
  FaUserClock,
  FaUserPlus,
  FaUserTimes,
  FaWhatsapp,
} from 'react-icons/fa';
import api, { formatDateTime, SALON_NAME } from '../services/api';
import { nyafazEngagementPath } from '../nyafazEngagementPaths';
import { AppointmentStatus, Loader, SourceBadge, StatCard, StatGrid, WelcomeBar } from '../components/ui';

/** 360 → "6 hours", 90 → "1 h 30 min", 45 → "45 min". */
function nudgeTime(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!m) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${h} h ${m} min`;
}

export default function Home() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .summary()
      .then(setData)
      .catch((e) => {
        setError(e.message);
        toast.error(`Backend not reachable: ${e.message}`);
      });
  }, []);

  if (!data && !error) return <Loader />;

  const c = data?.counts || {};
  const setup = data?.setup || {};
  const recent = data?.recent_appointments || [];

  const go = (segment) => () => navigate(nyafazEngagementPath(segment));
  // Three rows: what happened today, what needs staff now, and how the funnel is doing.
  const groups = [
    {
      title: 'Today',
      cards: [
        { label: "Today's bookings", value: c.today_bookings, icon: <FaCalendarDay />, onClick: go('appointments') },
        { label: 'New leads today', value: c.new_leads_today, icon: <FaUserPlus />, onClick: go('leads') },
        { label: 'Calls today', value: c.calls_today, icon: <FaPhoneAlt />, onClick: go('call-records') },
        {
          label: 'WhatsApp sent today',
          value: c.sent_today,
          icon: <FaPaperPlane />,
          sub: c.failed_today ? `${c.failed_today} failed` : null,
          onClick: go('reminders'),
        },
      ],
    },
    {
      title: 'Needs attention',
      cards: [
        {
          label: 'Did they come?',
          value: c.needs_update,
          icon: <FaUserClock />,
          sub: c.needs_update ? 'Still Booked after their time' : null,
          onClick: go('appointments'),
        },
        { label: 'Human intervention', value: c.open_handoffs, icon: <FaHandsHelping />, onClick: go('human-intervention') },
        { label: 'Waiting for a stylist', value: c.unassigned_upcoming, icon: <FaUserCheck />, onClick: go('appointments') },
        { label: 'Upcoming appointments', value: c.upcoming_appointments, icon: <FaCalendarCheck />, onClick: go('appointments') },
      ],
    },
    {
      title: 'Performance',
      cards: [
        {
          label: 'Lead → booking',
          value: `${c.conversion_pct ?? 0}%`,
          icon: <FaPercent />,
          sub:
            c.online_leads != null
              ? `${c.online_booked ?? 0} of ${c.online_leads} enquiries booked (walk-ins not counted)`
              : `${c.booked_leads ?? 0} of ${c.total_leads ?? 0} leads booked`,
          onClick: go('leads'),
        },
        { label: 'Total leads', value: c.total_leads, icon: <FaUsers />, onClick: go('leads') },
        {
          label: 'No-show rate (30 days)',
          value: `${c.no_show_rate_30d ?? 0}%`,
          icon: <FaCheckCircle />,
          sub: 'Missed out of finished appointments',
        },
        {
          label: 'No-shows (7 days)',
          value: c.no_shows_7d,
          icon: <FaUserTimes />,
          sub: c.no_shows_7d ? `${c.no_shows_rebooked_7d || 0} booked again` : null,
          onClick: go('appointments'),
        },
      ],
    },
  ];

  // Older backends only send the first nudge's minutes.
  const nudges = Array.isArray(setup.nudge_minutes)
    ? setup.nudge_minutes
    : setup.idle_nudge_minutes > 0
      ? [setup.idle_nudge_minutes]
      : [];
  const setupRows = [
    { label: 'Database', ok: !!setup.database, value: setup.database || 'Not connected' },
    { label: 'WhatsApp AI agent', ok: setup.openai, value: setup.openai ? 'Configured' : 'Key or model missing' },
    { label: 'WhatsApp sending', ok: setup.whatsapp, value: setup.whatsapp ? 'Configured' : 'Token or phone number id missing' },
    { label: 'Instagram DMs', ok: setup.instagram, value: setup.instagram ? 'Configured' : 'Instagram access token missing' },
    { label: 'Service file (AI docs)', ok: !!setup.rag_file, value: setup.rag_file || 'No file uploaded' },
    {
      label: 'Unfinished booking nudges',
      ok: nudges.length > 0,
      value: nudges.length
        ? `${nudges.length} nudge${nudges.length === 1 ? '' : 's'}: ${nudges.map(nudgeTime).join(', ')} after last message`
        : 'Off (turn on in Reminders → Automation Settings)',
    },
  ];

  return (
    <div>
      <WelcomeBar title={`${SALON_NAME} Dashboard`} sub="Customer engagement overview: WhatsApp bookings, voice calls and follow-ups." />

      {error ? <div className="nye-callout nye-callout--danger nye-mb-16">Backend not reachable: {error}</div> : null}

      {groups.map((group) => (
        <React.Fragment key={group.title}>
          <div className="nye-section-label" style={{ margin: '4px 0 8px' }}>
            {group.title}
          </div>
          <StatGrid columns={4} style={{ marginBottom: 16 }}>
            {group.cards.map((card) => (
              <StatCard key={card.label} label={card.label} value={card.value ?? 0} icon={card.icon} sub={card.sub} onClick={card.onClick} />
            ))}
          </StatGrid>
        </React.Fragment>
      ))}

      <div className="nye-g2">
        <div className="nye-card">
          <div className="nye-row nye-row--between" style={{ marginBottom: 6 }}>
            <div className="nye-card-title" style={{ marginBottom: 0 }}>
              Recent Appointments
            </div>
            <Link to={nyafazEngagementPath('appointments')} className="nye-small nye-purple-text" style={{ fontWeight: 700 }}>
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="nye-muted nye-small" style={{ padding: '20px 0', textAlign: 'center' }}>
              No appointments yet
            </div>
          ) : (
            recent.map((a) => (
              <div key={a.id} className="nye-line-item">
                <div style={{ minWidth: 0 }}>
                  <div className="nye-strong nye-ellipsis">{a.customer_name}</div>
                  <div className="nye-small nye-muted nye-ellipsis">
                    {a.staff_name || 'Stylist not assigned'} · {a.service_name}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div className="nye-row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                    <SourceBadge source={a.source} size={15} />
                    <AppointmentStatus status={a.status} />
                  </div>
                  <div className="nye-small nye-muted" style={{ marginTop: 3 }}>
                    {formatDateTime(a.starts_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="nye-card">
          <div className="nye-card-title">Lead Channels</div>
          <div className="nye-line-item">
            <span className="nye-row" style={{ gap: 8 }}>
              <FaWhatsapp size={16} color="#25D366" />
              <span className="nye-strong" style={{ color: '#1fa855' }}>
                WhatsApp bookings
              </span>
            </span>
            <span className="nye-strong">{c.whatsapp_appointments ?? 0}</span>
          </div>
          <div className="nye-line-item">
            <span className="nye-row" style={{ gap: 8 }}>
              <FaPhoneAlt size={13} color="#258a85" />
              <span className="nye-strong" style={{ color: '#258a85' }}>
                Voice bookings
              </span>
            </span>
            <span className="nye-strong">{c.sarvam_appointments ?? 0}</span>
          </div>
          <div className="nye-line-item">
            <span className="nye-muted">WhatsApp conversations</span>
            <span className="nye-strong">{c.conversations ?? 0}</span>
          </div>
          <div className="nye-line-item">
            <span className="nye-muted">Open leads (not booked yet)</span>
            <span className="nye-strong">{c.open_leads ?? 0}</span>
          </div>
          <div className="nye-line-item">
            <span className="nye-muted">Services taken</span>
            <span className="nye-strong nye-gold-text">{c.services_taken ?? 0}</span>
          </div>
          <div className="nye-line-item">
            <span className="nye-muted">Active staff</span>
            <span className="nye-strong">{c.active_staff ?? 0}</span>
          </div>
        </div>
      </div>

      <div className="nye-card nye-mt-16">
        <div className="nye-card-title">Setup</div>
        {setupRows.map((row) => (
          <div key={row.label} className="nye-line-item">
            <div>
              <div className="nye-strong">{row.label}</div>
              <div className="nye-small nye-muted">{row.value}</div>
            </div>
            <span className={`nye-pill ${row.ok ? 'nye-pill--ok' : 'nye-pill--warn'}`}>{row.ok ? 'Active' : 'Needs setup'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
